<template>
  <div class="space-y-6">
    <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
      <div>
        <h1 class="text-3xl font-black tracking-tight text-slate-900">Mes colis</h1>
        <p class="mt-2 text-slate-600 font-medium">Liste de vos commandes et suivi de vos colis.</p>
      </div>
      <div class="flex items-center gap-3">
        <button 
          @click="fetchCommandes" 
          class="h-11 px-5 rounded-xl border border-primary/20 text-primary font-bold hover:bg-primary/5 transition-colors flex items-center gap-2"
          :disabled="loading"
        >
          <span class="material-symbols-outlined text-xl" :class="{ 'animate-spin': loading }">refresh</span>
          Actualiser
        </button>
      </div>
    </div>

    <!-- Filtres & Recherche -->
    <div class="rounded-2xl bg-white border border-primary/10 p-5">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div class="relative">
          <span class="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">search</span>
          <input
            v-model="searchQuery"
            class="h-12 w-full rounded-xl border border-primary/15 bg-white pl-12 pr-4 text-sm font-semibold outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
            placeholder="Rechercher une commande ou facture..."
            type="text"
          />
        </div>
      </div>
    </div>

    <!-- Liste des commandes -->
    <div v-if="loading" class="py-20 text-center">
      <div class="inline-block size-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
      <p class="mt-4 text-slate-500 font-bold">Chargement de vos commandes...</p>
    </div>

    <div v-else-if="commandes.length === 0" class="rounded-3xl bg-white border border-primary/10 p-16 text-center">
      <div class="size-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-300">
        <span class="material-symbols-outlined text-5xl">inventory_2</span>
      </div>
      <h3 class="text-xl font-black text-slate-900">Aucune commande trouvée</h3>
      <p class="mt-2 text-slate-500 font-medium max-w-sm mx-auto">Vous n'avez pas encore de commandes enregistrées sur votre compte.</p>
    </div>

    <div v-else class="grid grid-cols-1 gap-6">
      <div v-for="commande in filteredCommandes" :key="commande.id" 
        class="group rounded-2xl bg-white border border-primary/10 overflow-hidden hover:shadow-xl hover:shadow-primary/5 transition-all">
        <div class="p-6">
          <div class="flex flex-col lg:flex-row justify-between gap-6">
            <div class="flex-1 space-y-4">
              <div class="flex items-start justify-between lg:justify-start lg:gap-6">
                <div>
                  <div class="text-xs font-black tracking-widest uppercase text-slate-400 mb-1">Référence</div>
                  <div class="text-lg font-black text-slate-900">{{ commande.name }}</div>
                  <div class="text-sm font-bold text-primary">{{ commande.commande.num_facture }}</div>
                </div>
                <div class="text-right lg:text-left">
                  <div class="text-xs font-black tracking-widest uppercase text-slate-400 mb-1">Statut</div>
                  <span class="status-chip" :class="statusClass(commande.statut)">
                    {{ statusLabel(commande.statut) }}
                  </span>
                </div>
              </div>

              <div class="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-2">
                <div>
                  <div class="text-xs font-black tracking-widest uppercase text-slate-400 mb-1">Date création</div>
                  <div class="text-sm font-bold text-slate-700">{{ commande.date_creation }}</div>
                </div>
                <div>
                  <div class="text-xs font-black tracking-widest uppercase text-slate-400 mb-1">Départ prévu</div>
                  <div class="text-sm font-bold text-slate-700">{{ commande.commande.date_depart }}</div>
                </div>
                <div>
                  <div class="text-xs font-black tracking-widest uppercase text-slate-400 mb-1">Nb Colis</div>
                  <div class="text-sm font-bold text-slate-700">{{ commande.details.nombre_colis }}</div>
                </div>
                <div>
                  <div class="text-xs font-black tracking-widest uppercase text-slate-400 mb-1">Poids total</div>
                  <div class="text-sm font-bold text-slate-700">{{ commande.details.poids_total }} kg</div>
                </div>
              </div>

              <!-- Timeline des étapes -->
              <div class="pt-6">
                <div class="flex items-center justify-between max-w-2xl">
                  <div v-for="(done, step) in commande.étapes" :key="step" class="flex flex-col items-center gap-2 flex-1 relative">
                    <!-- Ligne de connection -->
                    <div v-if="Object.keys(commande.étapes).indexOf(step) < Object.keys(commande.étapes).length - 1" 
                      class="absolute top-4 left-1/2 w-full h-0.5 bg-slate-100 -z-0">
                      <div class="h-full bg-primary transition-all duration-500" :style="{ width: done ? '100%' : '0%' }"></div>
                    </div>
                    
                    <div class="size-8 rounded-full flex items-center justify-center z-10 transition-colors"
                      :class="done ? 'bg-primary text-white' : 'bg-slate-100 text-slate-400'">
                      <span class="material-symbols-outlined text-sm">{{ stepIcon(step) }}</span>
                    </div>
                    <span class="text-[10px] font-black uppercase tracking-wider" 
                      :class="done ? 'text-primary' : 'text-slate-400'">
                      {{ stepLabel(step) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="lg:w-72 lg:pl-6 lg:border-l border-primary/5 flex flex-col justify-between gap-4">
              <div class="bg-slate-50 rounded-xl p-4 space-y-2">
                <div class="flex justify-between text-sm">
                  <span class="text-slate-500 font-bold">Valeur totale</span>
                  <span class="text-slate-900 font-black">{{ formatPrice(commande.details.valeur_totale) }}</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-slate-500 font-bold">Retard</span>
                  <span :class="commande.commande.retard > 0 ? 'text-red-600' : 'text-green-600'" class="font-black">
                    {{ commande.commande.retard }} jours
                  </span>
                </div>
                <!-- 🟢 Statut du Paiement -->
                <div v-if="commande.shipment?.payments?.length > 0" class="flex justify-between text-[10px] pt-1 border-t border-slate-200/50 mt-1">
                  <span class="text-slate-400 font-black uppercase tracking-wider">Paiement</span>
                  <span v-for="pay in commande.shipment.payments" :key="pay.id" 
                    :class="pay.status === 'succeeded' ? 'text-green-600' : 'text-amber-600'" 
                    class="font-black">
                    {{ pay.status === 'succeeded' ? '✓ Payé' : 'En attente' }} ({{ formatPrice(pay.amount, pay.currency) }})
                  </span>
                </div>
              </div>
              <div class="flex gap-2">
                <button 
                  v-if="commande.étapes?.arrivé && !commande.étapes?.reçu"
                  @click="startShipping(commande)"
                  :disabled="shipWorkflow.loading || shipWorkflow.stripe.processing"
                  class="flex-1 h-11 rounded-xl bg-indigo-600 text-white font-black shadow-lg shadow-indigo-200 text-sm hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100"
                >
                  <span class="material-symbols-outlined text-lg">
                    {{ isAlreadyShipped(commande) ? 'local_shipping' : 'send' }}
                  </span>
                  {{ isAlreadyShipped(commande) ? 'Suivre' : 'Expédier' }}
                </button>
                <RouterLink 
                  :to="{ name: 'app.commandes.details', params: { id: commande.id } }"
                  class="flex-1 h-11 rounded-xl bg-primary text-white font-black shadow-lg shadow-primary/20 text-sm hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center"
                >
                  Détails
                </RouterLink>
                <button class="size-11 flex items-center justify-center rounded-xl border border-primary/15 text-primary hover:bg-primary/5 transition-colors">
                  <span class="material-symbols-outlined text-xl">download</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Liste des colis (masquée par défaut, ou affichée au clic) -->
        <div class="bg-slate-50/50 border-t border-primary/5 px-6 py-4">
          <div class="flex items-center gap-2 text-xs font-black text-slate-400 uppercase tracking-widest mb-3">
            <span class="material-symbols-outlined text-sm">package_2</span>
            Détail des colis ({{ commande.colis.length }})
          </div>
          <div class="space-y-2">
            <div v-for="colis in commande.colis" :key="colis.id" 
              class="flex items-center justify-between p-3 bg-white rounded-xl border border-primary/5 text-sm">
              <div class="flex items-center gap-3">
                <div class="font-black text-slate-800">{{ colis.name }}</div>
                <div class="text-xs font-bold text-slate-400">#{{ colis.code_bar }}</div>
              </div>
              <div class="flex items-center gap-6">
                <div class="hidden sm:block text-xs font-bold text-slate-500">{{ colis.long }}x{{ colis.larg }}x{{ colis.haut }} cm</div>
                <div class="font-black text-slate-900">{{ colis.masse }} kg</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.last_page > 1" class="flex justify-center items-center gap-2 pt-4">
      <button 
        @click="changePage(pagination.current_page - 1)"
        :disabled="pagination.current_page === 1"
        class="size-10 flex items-center justify-center rounded-xl border border-primary/10 disabled:opacity-30"
      >
        <span class="material-symbols-outlined">chevron_left</span>
      </button>
      <div class="px-4 text-sm font-black text-slate-600">
        Page {{ pagination.current_page }} sur {{ pagination.last_page }}
      </div>
      <button 
        @click="changePage(pagination.current_page + 1)"
        :disabled="pagination.current_page === pagination.last_page"
        class="size-10 flex items-center justify-center rounded-xl border border-primary/10 disabled:opacity-30"
      >
        <span class="material-symbols-outlined">chevron_right</span>
      </button>
    </div>

    <!-- Modal Workflow Expédition -->
    <Teleport to="body">
      <div v-if="shipWorkflow.show" class="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6">
        <!-- Overlay flouté recouvrant TOUTE la page -->
        <div class="fixed inset-0 bg-slate-900/60 backdrop-blur-2xl transition-all duration-500"></div>
        
        <div class="relative w-full max-w-5xl h-[90vh] bg-white rounded-[40px] shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in duration-300">
        <!-- Header Modal -->
        <div class="px-10 py-8 bg-white border-b border-primary/5 flex items-center justify-between">
          <div class="flex items-center gap-4">
            <div>
              <h3 class="text-2xl font-black text-slate-900">Expédier votre commande</h3>
              <p class="text-base font-bold text-slate-500 mt-1">Suivez les étapes pour finaliser l'envoi de votre colis</p>
            </div>
            <div v-if="!isProduction && shipWorkflow.shipment?.is_test" class="px-4 py-2 bg-amber-100 text-amber-700 rounded-xl flex items-center gap-2 animate-pulse border border-amber-200">
              <span class="material-symbols-outlined text-xl">science</span>
              <span class="text-sm font-black uppercase tracking-wider">Mode Test</span>
            </div>
          </div>
          <!-- Suppression du bouton X pour forcer l'usage du bouton Annuler -->
        </div>

        <div class="flex-1 overflow-y-auto custom-scrollbar p-10">
          <!-- Stepper Agrandi -->
          <div class="flex items-center justify-between mb-16 px-10 max-w-3xl mx-auto">
            <div v-for="step in 3" :key="step" class="flex flex-col items-center gap-3 relative flex-1">
              <div v-if="step < 3" class="absolute top-6 left-1/2 w-full h-1 bg-slate-100 -z-0">
                <div class="h-full bg-primary transition-all duration-500" :style="{ width: shipWorkflow.step > step ? '100%' : '0%' }"></div>
              </div>
              <div class="size-12 rounded-full flex items-center justify-center z-10 transition-all duration-300 font-black text-lg"
                :class="shipWorkflow.step >= step ? 'bg-primary text-white scale-110 shadow-xl shadow-primary/20' : 'bg-slate-100 text-slate-400'">
                {{ step === 3 && shipWorkflow.step === 4 ? '✓' : step }}
              </div>
              <span class="text-xs font-black uppercase tracking-widest" :class="shipWorkflow.step >= step ? 'text-primary' : 'text-slate-400'">
                  {{ step === 1 ? 'Sélection' : step === 2 ? 'Paiement' : 'Expédition' }}
                </span>
            </div>
          </div>

          <!-- Étape 1 : Comparateur -->
          <div v-if="shipWorkflow.step === 1" class="space-y-8 max-w-4xl mx-auto">
            <div v-if="!isProduction && shipWorkflow.shipment?.is_test" class="p-6 rounded-3xl bg-amber-50 border border-amber-100 flex items-center gap-4">
              <div class="size-14 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center">
                <span class="material-symbols-outlined text-3xl">science</span>
              </div>
              <div>
                <div class="text-lg font-black text-amber-900">Environnement de test actif</div>
                <p class="text-sm font-bold text-amber-700/70">Aucun frais réel ne sera débité sur votre carte bancaire.</p>
              </div>
            </div>

            <!-- Adresse d'expédition (ship_from), saisie par le client -->
            <div class="rounded-[32px] border border-slate-100 bg-slate-50/50 p-6 sm:p-8 space-y-6">
              <h5 class="flex items-center gap-3 text-sm font-black uppercase tracking-widest text-slate-400">
                <span class="material-symbols-outlined text-xl">store</span>
                Adresse d'expédition (point de départ du colis)
              </h5>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="col-span-full space-y-1.5">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Nom / Raison sociale <span class="text-red-500">*</span></label>
                  <input v-model="shipWorkflow.fromAddress.name" placeholder="Ex: Entrepôt Guepard Paris" required
                    class="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold outline-none focus:border-primary" />
                </div>
                <div class="sm:col-span-1 space-y-1.5">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Adresse (rue) <span class="text-red-500">*</span></label>
                  <input v-model="shipWorkflow.fromAddress.address_line_1" placeholder="Ex: 15 Rue de la Paix" required
                    class="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold outline-none focus:border-primary" />
                </div>
                <div class="sm:col-span-1 space-y-1.5">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">N° <span class="text-red-500">*</span></label>
                  <input v-model="shipWorkflow.fromAddress.house_number" placeholder="Ex: 12 bis" required
                    class="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold outline-none focus:border-primary" />
                </div>
                <div class="sm:col-span-1 space-y-1.5">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Code Postal <span class="text-red-500">*</span></label>
                  <input v-model="shipWorkflow.fromAddress.postal_code" placeholder="Ex: 75001" required
                    class="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold outline-none focus:border-primary" />
                </div>
                <div class="sm:col-span-1 space-y-1.5">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Ville <span class="text-red-500">*</span></label>
                  <input v-model="shipWorkflow.fromAddress.city" placeholder="Ex: Paris" required
                    class="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold outline-none focus:border-primary" />
                </div>
                <div class="sm:col-span-1 space-y-1.5">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Pays</label>
                  <select v-model="shipWorkflow.fromAddress.country_code"
                    class="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold outline-none appearance-none focus:border-primary">
                    <option value="FR">France (FR)</option>
                    <option value="SN">Sénégal (SN)</option>
                    <option value="CI">Côte d'Ivoire (CI)</option>
                    <option value="CM">Cameroun (CM)</option>
                    <option value="US">États-Unis (US)</option>
                    <option value="BE">Belgique (BE)</option>
                    <option value="CA">Canada (CA)</option>
                    <option value="MA">Maroc (MA)</option>
                  </select>
                </div>
                <div class="sm:col-span-1 space-y-1.5">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Téléphone <span class="text-red-500">*</span></label>
                  <input v-model="shipWorkflow.fromAddress.phone_number" placeholder="Ex: +33612345678" required
                    class="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold outline-none focus:border-primary" />
                </div>
                <div class="sm:col-span-1 space-y-1.5">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Email <span class="text-red-500">*</span></label>
                  <input v-model="shipWorkflow.fromAddress.email" placeholder="Ex: contact@entrepot.com" required
                    class="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold outline-none focus:border-primary" />
                </div>
              </div>
            </div>

            <!-- Adresse du destinataire (to_address / ship_to), pré-remplie depuis la commande mais éditable -->
            <div class="rounded-[32px] border border-slate-100 bg-slate-50/50 p-6 sm:p-8 space-y-6">
              <h5 class="flex items-center gap-3 text-sm font-black uppercase tracking-widest text-slate-400">
                <span class="material-symbols-outlined text-xl">location_on</span>
                Adresse du destinataire
              </h5>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="col-span-full space-y-1.5">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Nom complet <span class="text-red-500">*</span></label>
                  <input v-model="shipWorkflow.address.name" placeholder="Ex: Jean Dupont" required
                    class="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold outline-none focus:border-primary" />
                </div>
                <div class="sm:col-span-1 space-y-1.5">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Adresse (rue) <span class="text-red-500">*</span></label>
                  <input v-model="shipWorkflow.address.address_line_1" placeholder="Ex: 15 Rue de la Paix" required
                    class="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold outline-none focus:border-primary" />
                </div>
                <div class="sm:col-span-1 space-y-1.5">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">N° de maison <span class="text-red-500">*</span></label>
                  <input v-model="shipWorkflow.address.house_number" placeholder="Ex: 12 bis" required
                    class="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold outline-none focus:border-primary" />
                </div>
                <div class="sm:col-span-1 space-y-1.5">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Code Postal <span class="text-red-500">*</span></label>
                  <input v-model="shipWorkflow.address.postal_code" placeholder="Ex: 75001" required
                    class="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold outline-none focus:border-primary" />
                </div>
                <div class="sm:col-span-1 space-y-1.5">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Ville <span class="text-red-500">*</span></label>
                  <input v-model="shipWorkflow.address.city" placeholder="Ex: Paris" required
                    class="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold outline-none focus:border-primary" />
                </div>
                <div class="sm:col-span-1 space-y-1.5">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Pays</label>
                  <select v-model="shipWorkflow.address.country_code"
                    class="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold outline-none appearance-none focus:border-primary">
                    <option value="FR">France (FR)</option>
                    <option value="SN">Sénégal (SN)</option>
                    <option value="CI">Côte d'Ivoire (CI)</option>
                    <option value="CM">Cameroun (CM)</option>
                    <option value="US">États-Unis (US)</option>
                    <option value="BE">Belgique (BE)</option>
                    <option value="CA">Canada (CA)</option>
                    <option value="MA">Maroc (MA)</option>
                  </select>
                </div>
                <div class="sm:col-span-1 space-y-1.5">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Téléphone <span class="text-red-500">*</span></label>
                  <input v-model="shipWorkflow.address.phone_number" placeholder="Ex: +33612345678" required
                    class="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold outline-none focus:border-primary" />
                </div>
                <div class="sm:col-span-1 space-y-1.5">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Email <span class="text-red-500">*</span></label>
                  <input v-model="shipWorkflow.address.email" placeholder="Ex: client@email.com" required
                    class="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold outline-none focus:border-primary" />
                </div>
              </div>
            </div>

            <!-- Regroupement > 3 colis : le client ne gère plus, l'admin doit valider -->
            <div v-if="requiresAdminGrouping && !auth.isAdmin && !shipWorkflow.pendingAdminGrouping" class="p-4 rounded-2xl bg-amber-50 border border-amber-100 text-amber-700 text-sm font-bold flex items-center gap-3">
              <span class="material-symbols-outlined">groups</span>
              Cette commande contient {{ shipWorkflow.colis.length }} colis. Au-delà de 3 colis, le regroupement doit être validé par un administrateur.
            </div>

            <div v-if="shipWorkflow.pendingAdminGrouping" class="p-4 rounded-2xl bg-blue-50 border border-blue-100 text-blue-700 text-sm font-bold flex items-center gap-3">
              <span class="material-symbols-outlined">hourglass_top</span>
              Demande de regroupement envoyée : un administrateur va la traiter prochainement.
            </div>

            <button
              v-if="!shipWorkflow.pendingAdminGrouping"
              @click="searchOffers"
              :disabled="shipWorkflow.loading"
              class="h-12 px-8 rounded-xl bg-primary text-white font-black shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-2 disabled:opacity-50"
            >
              <span v-if="shipWorkflow.loading" class="size-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></span>
              <span v-else class="material-symbols-outlined text-lg">{{ requiresAdminGrouping && !auth.isAdmin ? 'send' : 'search' }}</span>
              {{ requiresAdminGrouping && !auth.isAdmin ? 'Envoyer la demande de regroupement' : (shipWorkflow.searchedOffers ? 'Actualiser les offres' : 'Rechercher les offres de transport') }}
            </button>

            <div v-if="shipWorkflow.searchedOffers" class="space-y-8">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-primary/5 pb-6">
                <div>
                  <h4 class="text-xl font-black text-slate-900">Offres disponibles</h4>
                  <p class="text-sm font-bold text-slate-500 mt-1">Choisissez votre transporteur</p>
                </div>
                <div class="flex items-center gap-3">
                  <div class="flex flex-col items-end">
                    <span class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Poids</span>
                    <span class="px-4 py-1.5 bg-primary/5 text-primary border border-primary/10 rounded-xl text-sm font-black uppercase">
                      {{ shipWorkflow.shipment?.weight }} kg
                    </span>
                  </div>
                </div>
              </div>

              <!-- Filtres -->
              <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div class="relative">
                  <span class="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">public</span>
                  <select
                    v-model="shipWorkflow.filters.country"
                    @change="updateCarriers"
                    class="h-11 w-full rounded-xl border border-primary/15 bg-slate-50/50 pl-11 pr-4 text-xs font-bold outline-none appearance-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                  >
                    <option value="FR">France (FR)</option>
                    <option value="SN">Sénégal (SN)</option>
                    <option value="CI">Côte d'Ivoire (CI)</option>
                    <option value="CM">Cameroun (CM)</option>
                    <option value="US">États-Unis (US)</option>
                    <option value="BE">Belgique (BE)</option>
                    <option value="CA">Canada (CA)</option>
                    <option value="MA">Maroc (MA)</option>
                  </select>
                </div>
                <div class="relative">
                  <span class="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">local_shipping</span>
                  <input
                    v-model="shipWorkflow.filters.carrier"
                    placeholder="Transporteur..."
                    class="h-11 w-full rounded-xl border border-primary/15 bg-slate-50/50 pl-11 pr-4 text-xs font-bold outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                  />
                </div>
                <div class="relative">
                  <span class="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">sort</span>
                  <select
                    v-model="shipWorkflow.filters.sortBy"
                    class="h-11 w-full rounded-xl border border-primary/15 bg-slate-50/50 pl-11 pr-4 text-xs font-bold outline-none appearance-none focus:border-primary"
                  >
                    <option value="">Ordre par défaut</option>
                    <option value="price">Trier par : Prix bas</option>
                    <option value="delay">Trier par : Délai court</option>
                  </select>
                </div>
                <div class="relative">
                  <span class="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">payments</span>
                  <input
                    v-model.number="shipWorkflow.filters.maxPrice"
                    type="number"
                    placeholder="Budget max..."
                    class="h-11 w-full rounded-xl border border-primary/15 bg-slate-50/50 pl-11 pr-4 text-xs font-bold outline-none focus:border-primary"
                  />
                </div>
              </div>

              <!-- Message d'erreur local (ex: Poids trop élevé) -->
              <div v-if="shipWorkflow.error" class="p-4 rounded-2xl bg-red-50 border border-red-100 text-red-600 text-sm font-bold flex items-center gap-3 animate-in fade-in slide-in-from-top-2">
                <span class="material-symbols-outlined">error</span>
                {{ shipWorkflow.error }}
              </div>

              <div v-if="shipWorkflow.loading" class="py-20 text-center">
                <div class="inline-block size-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                <p class="mt-4 text-slate-500 font-bold">Calcul des meilleurs tarifs...</p>
              </div>

              <div v-else-if="filteredMethods.length === 0" class="py-20 text-center rounded-3xl bg-slate-50 border-2 border-dashed border-slate-200">
                <span class="material-symbols-outlined text-4xl text-slate-300">filter_list_off</span>
                <p class="mt-4 text-slate-500 font-bold">Aucune offre disponible.</p>
              </div>

              <div v-else class="grid grid-cols-1 gap-4">
                <button
                  v-for="method in filteredMethods" :key="method.id"
                  @click="selectMethod(method)"
                  class="flex items-center justify-between p-6 rounded-[28px] border-2 transition-all group text-left"
                  :class="shipWorkflow.selectedMethod?.id === method.id ? 'border-primary bg-primary/5 ring-4 ring-primary/5' : 'border-slate-100 hover:border-primary/30 hover:bg-slate-50'"
                >
                  <div class="flex items-center gap-6">
                    <div class="size-16 rounded-2xl bg-white border border-slate-100 flex items-center justify-center overflow-hidden">
                      <img v-if="method.carrier_logo" :src="method.carrier_logo" class="size-full object-contain p-2" />
                      <span v-else class="font-black text-slate-400">{{ method.carrier?.substring(0,3).toUpperCase() }}</span>
                    </div>
                    <div>
                      <div class="text-lg font-black text-slate-900">{{ method.name }}</div>
                      <div class="flex items-center gap-2">
                        <div class="text-sm font-bold text-slate-500">{{ method.carrier }} — ~{{ method.delivery_time }}h</div>
                        <div v-if="method.max_weight" class="px-2 py-0.5 rounded-md bg-slate-100 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                          Max {{ method.max_weight }}kg
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="text-2xl font-black text-primary">{{ formatPrice(method.final_price || method.price || 0, 'EUR') }}</div>
                    <div class="text-[10px] font-black text-slate-400 uppercase tracking-widest">TTC</div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          <!-- Étape 2 : Récapitulatif -->
            <div v-if="shipWorkflow.step === 2" class="space-y-8 max-w-4xl mx-auto">
              <div v-if="!isProduction && shipWorkflow.shipment?.is_test" class="p-4 rounded-2xl bg-amber-50 border border-amber-100 text-amber-700 text-sm font-bold flex items-center gap-3">
                <span class="material-symbols-outlined">science</span>
                Mode Test actif : Aucun frais réel ne sera facturé.
              </div>
              
              <div class="text-center space-y-2 mb-10">
                <h4 class="text-3xl font-black text-slate-900">Vérifiez les informations</h4>
                <p class="text-slate-500 font-bold text-lg">Détails de livraison vers {{ shipWorkflow.filters.country }}</p>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <!-- Bloc Adresses (lecture seule — confirmées à l'étape précédente) -->
                <div class="space-y-6">
                  <div class="rounded-[32px] border border-slate-100 bg-slate-50/50 p-8 space-y-4">
                    <h5 class="flex items-center gap-3 text-sm font-black uppercase tracking-widest text-slate-400">
                      <span class="material-symbols-outlined text-xl">location_on</span>
                      Adresse du destinataire
                    </h5>
                    <div class="text-sm font-bold text-slate-700 space-y-1">
                      <div class="text-base font-black text-slate-900">{{ shipWorkflow.address.name }}</div>
                      <div>{{ shipWorkflow.address.house_number }} {{ shipWorkflow.address.address_line_1 }}</div>
                      <div>{{ shipWorkflow.address.postal_code }} {{ shipWorkflow.address.city }}, {{ shipWorkflow.address.country_code }}</div>
                      <div class="text-slate-500">{{ shipWorkflow.address.phone_number }} — {{ shipWorkflow.address.email }}</div>
                    </div>
                  </div>

                  <div class="rounded-[32px] border border-slate-100 bg-slate-50/50 p-8 space-y-4">
                    <h5 class="flex items-center gap-3 text-sm font-black uppercase tracking-widest text-slate-400">
                      <span class="material-symbols-outlined text-xl">store</span>
                      Adresse d'expédition
                    </h5>
                    <div class="text-sm font-bold text-slate-700 space-y-1">
                      <div class="text-base font-black text-slate-900">{{ shipWorkflow.fromAddress.name }}</div>
                      <div>{{ shipWorkflow.fromAddress.house_number }} {{ shipWorkflow.fromAddress.address_line_1 }}</div>
                      <div>{{ shipWorkflow.fromAddress.postal_code }} {{ shipWorkflow.fromAddress.city }}, {{ shipWorkflow.fromAddress.country_code }}</div>
                      <div class="text-slate-500">{{ shipWorkflow.fromAddress.phone_number }} — {{ shipWorkflow.fromAddress.email }}</div>
                    </div>
                  </div>
                </div>

                <div class="space-y-6">
                  <!-- Bloc Commande -->
                  <div class="rounded-3xl border border-slate-100 bg-slate-50/50 p-8 space-y-6">
                    <div class="flex justify-between items-center">
                      <h5 class="text-sm font-black uppercase tracking-widest text-slate-400">Contenu de l'envoi</h5>
                      <span class="px-3 py-1 bg-white border border-slate-200 rounded-full text-[10px] font-black text-slate-500">
                        {{ shipWorkflow.colis?.length || 0 }} {{ (shipWorkflow.colis?.length || 0) > 1 ? 'colis' : 'colis' }}
                      </span>
                    </div>

                    <!-- Liste détaillée des colis -->
                    <div class="space-y-4">
                      <div v-for="(colis, idx) in shipWorkflow.colis" :key="idx" 
                           class="bg-white rounded-2xl border border-slate-100 p-4 flex items-start gap-4">
                        <div class="size-10 rounded-xl bg-primary/5 text-primary flex items-center justify-center flex-shrink-0">
                          <span class="material-symbols-outlined text-xl">package_2</span>
                        </div>
                        <div class="flex-1 min-w-0">
                          <div class="flex justify-between items-start mb-1">
                            <div class="text-xs font-black text-slate-900 truncate">Colis #{{ idx + 1 }}</div>
                            <div class="text-[10px] font-black text-primary bg-primary/5 px-2 py-0.5 rounded">{{ colis.weight || 0.1 }} kg</div>
                          </div>
                          <p class="text-[10px] font-bold text-slate-500 line-clamp-1 mb-2">{{ colis.description || 'Sans description' }}</p>
                          <div class="flex items-center gap-3">
                            <div class="text-[10px] font-black text-slate-400 flex items-center gap-1">
                              <span class="material-symbols-outlined text-xs">payments</span>
                              {{ formatPrice(colis.value || 0, 'EUR') }}
                            </div>
                            <div v-if="colis.quantity" class="text-[10px] font-black text-slate-400 flex items-center gap-1">
                              <span class="material-symbols-outlined text-xs">numbers</span>
                              Qté: {{ colis.quantity }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="space-y-3 pt-4 border-t border-slate-200/50">
                      <div class="flex justify-between text-sm">
                        <span class="text-slate-500 font-bold">Référence commande</span>
                        <span class="font-black text-slate-900">{{ shipWorkflow.currentOrder?.commande?.num_facture }}</span>
                      </div>
                      <div class="flex justify-between text-sm">
                        <span class="text-slate-500 font-bold">Poids total combiné</span>
                        <span class="font-black text-slate-900">{{ shipWorkflow.shipment?.weight }} kg</span>
                      </div>
                    </div>
                  </div>

                  <!-- Bloc Prix -->
                  <div class="rounded-3xl border border-primary/10 bg-white p-8 shadow-xl shadow-primary/5 space-y-6">
                    <h5 class="text-sm font-black uppercase tracking-widest text-primary">Transporteur choisi</h5>
                    <div class="flex items-center gap-4">
                      <div class="size-12 rounded-xl bg-slate-50 flex items-center justify-center overflow-hidden">
                        <img v-if="shipWorkflow.selectedMethod?.carrier_logo" :src="shipWorkflow.selectedMethod.carrier_logo" class="size-full object-contain p-2" />
                      </div>
                      <div class="font-black text-slate-900">{{ shipWorkflow.selectedMethod?.name }}</div>
                    </div>
                    
                    <div class="space-y-3 pt-4 border-t border-slate-100">
                      <div class="flex justify-between text-sm">
                        <span class="text-slate-500 font-bold">Transport</span>
                        <span class="font-black text-slate-900">{{ formatPrice(shipWorkflow.selectedMethod?.original_price || 0, 'EUR') }}</span>
                      </div>
                      <div class="flex justify-between text-sm">
                        <span class="text-slate-500 font-bold">Frais de service</span>
                        <span class="font-black text-slate-900">{{ formatPrice(shipWorkflow.selectedMethod?.service_fee || 6, 'EUR') }}</span>
                      </div>
                      <!-- 🟢 Frais de regroupement (si >= 2 colis) -->
                      <div v-if="parcelGroups.length > 0" class="flex flex-col gap-1">
                        <div v-for="(group, idx) in parcelGroups" :key="idx" class="flex justify-between text-sm">
                          <span class="text-slate-500 font-bold">
                            Regroupement #{{ idx + 1 }} ({{ group.length }} colis)
                          </span>
                          <span class="font-black text-slate-900">{{ formatPrice(idx === 0 ? 6 : 3.6, 'EUR') }}</span>
                        </div>
                        <div class="flex justify-between text-sm pt-1">
                          <span class="text-slate-500 font-bold">Total regroupements</span>
                          <span class="font-black text-slate-900">{{ formatPrice(consolidationFee || 0, 'EUR') }}</span>
                        </div>
                        <div class="mt-2 p-3 bg-slate-50 rounded-xl border border-slate-100">
                          <div class="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-2">
                            <span class="material-symbols-outlined text-sm">inventory_2</span>
                            {{ parcelGroups.length }} {{ parcelGroups.length > 1 ? 'étiquettes' : 'étiquette' }} de transport {{ parcelGroups.length > 1 ? 'seront créées' : 'sera créée' }}
                          </div>
                        </div>
                      </div>
                      <div class="flex justify-between text-sm pt-2 border-t border-slate-50">
                        <span class="text-slate-500 font-bold">Total HT</span>
                        <span class="font-black text-slate-900">{{ formatPrice((shipWorkflow.selectedMethod?.final_price || 0) / 1.2, 'EUR') }}</span>
                      </div>
                      <div class="flex justify-between text-sm">
                        <span class="text-slate-500 font-bold">TVA (20 %)</span>
                        <span class="font-black text-slate-900">{{ formatPrice((shipWorkflow.selectedMethod?.final_price || 0) - ((shipWorkflow.selectedMethod?.final_price || 0) / 1.2), 'EUR') }}</span>
                      </div>
                    </div>

                    <div class="pt-6 border-t-2 border-dashed border-slate-100 flex justify-between items-end">
                      <div>
                        <div class="text-slate-400 font-black uppercase text-[10px] tracking-widest mb-1">Total TTC à payer</div>
                        <div class="text-4xl font-black text-primary">{{ formatPrice(shipWorkflow.selectedMethod?.final_price || 0, 'EUR') }}</div>
                      </div>
                    </div>
                  </div>

                  <!-- Bloc Erreurs (Étape 2) -->
                  <div v-if="shipWorkflow.stripe.error" class="p-4 rounded-2xl bg-red-50 border border-red-100 text-red-600 text-sm font-bold flex items-center gap-3">
                    <span class="material-symbols-outlined">error</span>
                    {{ shipWorkflow.stripe.error }}
                  </div>
                </div>
              </div>
            </div>

          <!-- Étape 3 : Paiement Stripe -->
            <div v-if="shipWorkflow.step === 3" class="space-y-8 max-w-lg mx-auto py-10">
              <div v-if="!isProduction && shipWorkflow.shipment?.is_test" class="p-4 rounded-2xl bg-amber-50 border border-amber-100 text-amber-700 text-sm font-bold flex items-center gap-3 mb-6">
                <span class="material-symbols-outlined">science</span>
                Mode Test : Utilisez une carte de test Stripe.
              </div>
              <div class="text-center space-y-2 mb-10">
              <div class="size-20 bg-indigo-50 text-indigo-600 rounded-3xl flex items-center justify-center mx-auto mb-4">
                <span class="material-symbols-outlined text-4xl">payments</span>
              </div>
              <h4 class="text-2xl font-black text-slate-900">Paiement sécurisé</h4>
              <p class="text-slate-500 font-bold">Réglez vos frais d'expédition par carte bancaire</p>
            </div>

            <div class="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm">
              <div id="payment-element" class="p-4 border border-slate-200 rounded-xl bg-slate-50/50">
                <!-- Stripe Payment Element will be mounted here -->
              </div>
              
              <div v-if="shipWorkflow.stripe.error" class="mt-4 p-4 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm font-bold">
                {{ shipWorkflow.stripe.error }}
              </div>

              <button 
                @click="confirmPayment"
                :disabled="shipWorkflow.stripe.processing"
                class="w-full mt-8 h-14 rounded-2xl bg-indigo-600 text-white font-black shadow-xl shadow-indigo-900/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3 disabled:opacity-50"
              >
                <span v-if="shipWorkflow.stripe.processing" class="size-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></span>
                <span v-else class="material-symbols-outlined text-xl">lock</span>
                Payer {{ formatPrice(shipWorkflow.selectedMethod?.final_price || shipWorkflow.selectedMethod?.price || 0, 'EUR') }}
              </button>
            </div>

            <div class="flex items-center justify-center gap-4 opacity-40 grayscale">
              <img src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg" class="h-6" alt="Stripe" />
              <span class="w-px h-4 bg-slate-300"></span>
              <div class="flex gap-2">
                <span class="material-symbols-outlined text-xl">credit_card</span>
                <span class="material-symbols-outlined text-xl">account_balance</span>
              </div>
            </div>
          </div>

          <!-- Étape 4 : Succès & Tracking (Polling) -->
          <div v-if="shipWorkflow.step === 4" class="space-y-10 py-10 max-w-2xl mx-auto text-center">
            <!-- État : Erreur lors de la génération -->
            <div v-if="shipWorkflow.stripe.error" class="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div class="size-32 bg-red-50 text-red-600 rounded-[40px] flex items-center justify-center mx-auto mb-6">
                <span class="material-symbols-outlined text-6xl">error</span>
              </div>
              <div class="space-y-2">
                <h4 class="text-3xl font-black text-slate-900">Oups, une erreur est survenue</h4>
                <p class="text-lg font-medium text-red-500">{{ shipWorkflow.stripe.error }}</p>
              </div>
              
              <div class="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  v-if="shipWorkflow.stripe.paymentIntentId"
                  @click="shipWorkflow.stripe.error = null; startPollingShipment(shipWorkflow.stripe.paymentIntentId)"
                  class="h-14 px-10 rounded-2xl bg-indigo-600 text-white font-black shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center gap-2"
                >
                  <span class="material-symbols-outlined">refresh</span>
                  Réessayer la vérification
                </button>
                <button 
                  v-if="!shipWorkflow.stripe.paymentIntentId"
                  @click="shipWorkflow.step = 2; shipWorkflow.stripe.error = null"
                  class="h-14 px-10 rounded-2xl bg-slate-900 text-white font-black shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center gap-2"
                >
                  <span class="material-symbols-outlined">edit</span>
                  Modifier les informations
                </button>
              </div>
            </div>

            <!-- État : Chargement / Polling -->
            <div v-else-if="shipWorkflow.shipment?.status !== 'shipped' && !shipWorkflow.tracking?.label_url" class="space-y-8">
              <div class="size-32 bg-indigo-50 text-indigo-600 rounded-[40px] flex items-center justify-center mx-auto mb-6 relative overflow-hidden">
                <div class="absolute inset-0 bg-indigo-600/10 animate-pulse"></div>
                <span class="material-symbols-outlined text-6xl animate-bounce">rocket_launch</span>
              </div>
              <div class="space-y-2">
                <h4 class="text-3xl font-black text-slate-900">Préparation de l'étiquette...</h4>
                <p class="text-lg font-medium text-slate-500">
                  Paiement validé ! Nous générons vos documents d'expédition chez {{ shipWorkflow.selectedMethod?.carrier }}.
                </p>
              </div>
              <div class="flex items-center justify-center gap-2">
                <div class="size-2 bg-indigo-600 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                <div class="size-2 bg-indigo-600 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                <div class="size-2 bg-indigo-600 rounded-full animate-bounce"></div>
              </div>
            </div>

            <div v-else class="space-y-8 animate-in fade-in zoom-in duration-500">
              <div class="size-32 bg-green-50 text-green-600 rounded-[40px] flex items-center justify-center mx-auto mb-6">
                <span class="material-symbols-outlined text-6xl">check_circle</span>
              </div>
              <div class="space-y-2">
                <h4 class="text-4xl font-black text-slate-900">Expédition validée !</h4>
                <p v-if="shipWorkflow.shipment?.status === 'pending'" class="text-lg font-medium text-amber-600 animate-pulse">Votre étiquette est en cours de préparation...</p>
                <p v-else class="text-lg font-medium text-slate-500">Votre colis est prêt. Téléchargez votre étiquette ci-dessous.</p>
              </div>

              <div class="bg-slate-50 rounded-[32px] border border-slate-100 p-8 space-y-6 text-left">
                  <div class="flex flex-wrap gap-3 mb-2">
                    <div v-if="!isProduction && shipWorkflow.shipment?.is_test" class="px-4 py-2 bg-amber-100 text-amber-700 rounded-xl inline-flex items-center gap-2 border border-amber-200">
                      <span class="material-symbols-outlined text-sm">science</span>
                      <span class="text-[10px] font-black uppercase tracking-wider">Expédition de Test</span>
                    </div>
                    <!-- 🟢 Badge Statut du Paiement -->
                    <div v-if="successfulPayment" class="px-4 py-2 bg-green-100 text-green-700 rounded-xl inline-flex items-center gap-2 border border-green-200">
                      <span class="material-symbols-outlined text-sm">verified</span>
                      <span class="text-[10px] font-black uppercase tracking-wider">
                        Paiement confirmé ({{ formatPrice(successfulPayment.amount, successfulPayment.currency) }})
                      </span>
                    </div>
                    <div v-else-if="shipWorkflow.payments?.length > 0" class="px-4 py-2 bg-slate-100 text-slate-500 rounded-xl inline-flex items-center gap-2 border border-slate-200">
                      <span class="material-symbols-outlined text-sm">hourglass_empty</span>
                      <span class="text-[10px] font-black uppercase tracking-wider">Traitement du paiement...</span>
                    </div>
                  </div>
                  <div class="flex flex-col gap-6 border-b border-slate-100 pb-6">
                    <div class="flex justify-between items-start">
                      <div>
                        <div class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Transporteur</div>
                        <div class="text-xl font-black text-slate-900">{{ getCarrierName(shipWorkflow.shipment) }}</div>
                      </div>
                      <div class="text-right">
                        <div class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                          {{ (shipWorkflow.shipment?.tracking_numbers?.length || 0) > 1 ? 'Numéros de suivi' : 'Numéro de suivi' }}
                        </div>
                        <div v-if="shipWorkflow.shipment?.tracking_numbers?.length" class="flex flex-col items-end gap-1">
                          <div v-for="tn in shipWorkflow.shipment.tracking_numbers" :key="tn" class="text-sm font-black text-primary font-mono bg-primary/5 px-2 py-0.5 rounded">
                            {{ tn }}
                          </div>
                        </div>
                        <div v-else class="text-xl font-black text-slate-900">
                          {{ shipWorkflow.tracking?.tracking_number || 'En attente...' }}
                        </div>
                      </div>
                    </div>

                    <div class="flex items-center gap-3">
                      <!-- 🟢 On affiche le bouton dès qu'on a au moins une URL, peu importe le statut -->
                      <div v-if="shipWorkflow.tracking?.label_url || (shipWorkflow.shipment?.label_urls && shipWorkflow.shipment.label_urls.length > 0)" class="flex-1 space-y-3">
                        <button 
                          @click="downloadLabel"
                          class="w-full h-12 rounded-xl bg-primary text-white font-black text-sm hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-lg shadow-primary/20"
                        >
                          <span class="material-symbols-outlined text-xl">download</span>
                          {{ (shipWorkflow.shipment?.label_urls?.length || 0) > 1 ? 'Tout télécharger (' + shipWorkflow.shipment.label_urls.length + ')' : 'Étiquette PDF' }}
                        </button>
                        
                        <!-- Liste des étiquettes individuelles si multi-colis -->
                        <div v-if="(shipWorkflow.shipment?.label_urls?.length || 0) > 1" class="grid grid-cols-2 gap-2">
                          <a v-for="(url, idx) in shipWorkflow.shipment.label_urls" :key="idx" :href="url" target="_blank"
                             class="h-10 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center gap-2 text-[10px] font-black text-slate-500 hover:bg-slate-100 transition-all">
                            <span class="material-symbols-outlined text-sm text-primary">description</span>
                            Étiquette #{{ idx + 1 }}
                          </a>
                        </div>
                      </div>
                      <!-- 🟡 Sinon, on affiche l'état de préparation si l'expédition est en cours -->
                      <div v-else class="flex-1 h-12 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest">
                        <span class="size-4 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin"></span>
                        Préparation des étiquettes...
                      </div>
                    </div>
                  </div>

                  <!-- Timeline de suivi avec barre de progression -->
                  <div v-if="shipWorkflow.tracking?.statuses?.length" class="space-y-4 pt-2">
                    <div class="flex items-center justify-between mb-4">
                      <div class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Historique de l'envoi</div>
                      <div class="h-1.5 w-32 bg-slate-100 rounded-full overflow-hidden">
                        <div class="h-full bg-primary transition-all duration-1000" :style="{ width: progressPercentage + '%' }"></div>
                      </div>
                    </div>
                    <div class="relative space-y-6 before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-100">
                      <div v-for="(status, idx) in shipWorkflow.tracking.statuses" :key="idx" class="relative pl-10 flex flex-col items-start text-left">
                        <div class="absolute left-0 top-1 size-[24px] rounded-full border-4 border-white shadow-sm flex items-center justify-center"
                          :class="idx === 0 ? 'bg-primary' : 'bg-slate-200'">
                          <div class="size-1.5 rounded-full bg-white"></div>
                        </div>
                        <div class="text-xs font-black text-slate-900 leading-tight mb-0.5">{{ status.message }}</div>
                        <div v-if="status.description" class="text-[10px] font-bold text-slate-500 mb-1">{{ status.description }}</div>
                        <div class="flex items-center gap-2">
                          <div class="text-[10px] font-bold text-slate-400">{{ new Date(status.created_at).toLocaleString('fr-FR') }}</div>
                          <div v-if="status.location" class="text-[10px] font-black text-primary uppercase tracking-wider">• {{ status.location }}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Fallback : Expédition non trouvée -->
                  <div v-else-if="shipWorkflow.tracking.notFound" class="py-10 text-center bg-slate-100/50 rounded-3xl border-2 border-dashed border-slate-200">
                    <span class="material-symbols-outlined text-4xl text-slate-300 mb-2">pending_actions</span>
                    <div class="text-sm font-black text-slate-500">Expédition non encore préparée</div>
                    <p class="text-[10px] font-bold text-slate-400 mt-1">Le suivi sera disponible dès que le colis sera scanné.</p>
                  </div>

                  <button 
                    @click="closeShipWorkflow"
                    class="h-14 w-full rounded-2xl bg-slate-900 text-white font-black shadow-xl shadow-slate-200 hover:scale-[1.02] active:scale-[0.98] transition-all"
                  >
                    Retour à mes colis
                  </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer Modal -->
        <div v-if="shipWorkflow.step < 4" class="px-10 py-8 bg-slate-50/50 border-t border-primary/5 flex items-center justify-between">
          <button 
            @click="closeShipWorkflow" 
            class="h-14 px-10 rounded-2xl font-black text-slate-500 hover:bg-slate-200 transition-all flex items-center gap-2"
          >
            <span class="material-symbols-outlined">close</span>
            Annuler
          </button>
          
          <div class="flex items-center gap-4">
            <button 
              v-if="shipWorkflow.step === 1 && shipWorkflow.selectedMethod"
              @click="confirmCarrier"
              :disabled="shipWorkflow.loading"
              class="h-14 px-10 rounded-2xl bg-primary text-white font-black shadow-xl shadow-primary/20 hover:scale-105 active:scale-95 transition-all flex items-center gap-3"
            >
              <span v-if="shipWorkflow.loading" class="size-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></span>
              Continuer
              <span class="material-symbols-outlined text-xl">arrow_forward</span>
            </button>

            <button 
              v-if="shipWorkflow.step === 2"
              @click="initStripeForm"
              :disabled="shipWorkflow.loading"
              class="h-14 px-10 rounded-2xl bg-primary text-white font-black shadow-xl shadow-primary/20 hover:scale-105 active:scale-95 transition-all flex items-center gap-3"
            >
              <span v-if="shipWorkflow.loading" class="size-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></span>
              <span class="material-symbols-outlined text-xl">payments</span>
              Procéder au paiement
            </button>
          </div>
        </div>
      </div>
    </div>
    </Teleport>

    <!-- 🟢 Popup de notification (Toast) -->
    <Teleport to="body">
      <Transition
        enter-active-class="transform ease-out duration-300 transition"
        enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div v-if="shipWorkflow.toast.show" class="fixed top-10 right-10 z-[9999] max-w-md w-full">
          <div class="bg-white rounded-[24px] shadow-2xl border border-slate-100 p-5 flex items-start gap-4 overflow-hidden relative">
            <!-- Barre de progression de fermeture -->
            <div v-if="shipWorkflow.toast.show" class="absolute bottom-0 left-0 h-1 bg-red-500 toast-progress-bar"></div>
            
            <div class="size-12 rounded-2xl flex-shrink-0 flex items-center justify-center"
              :class="shipWorkflow.toast.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'">
              <span class="material-symbols-outlined text-2xl">
                {{ shipWorkflow.toast.type === 'error' ? 'error' : 'check_circle' }}
              </span>
            </div>
            
            <div class="flex-1 pt-1">
              <h6 class="text-sm font-black text-slate-900 mb-1">
                {{ shipWorkflow.toast.type === 'error' ? 'Erreur de validation' : 'Succès' }}
              </h6>
              <p class="text-xs font-bold text-slate-500 leading-relaxed">{{ shipWorkflow.toast.message }}</p>
            </div>

            <button @click="shipWorkflow.toast.show = false" class="text-slate-300 hover:text-slate-900 transition-colors">
              <span class="material-symbols-outlined text-xl">close</span>
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth'
import axios from 'axios'
import ugoShippingWorkflow from '@/mixins/ugoShippingWorkflow'

export default {
  name: 'CommandesPage',
  mixins: [ugoShippingWorkflow],
  data() {
    return {
      loading: false,
      commandes: [],
      pagination: {
        current_page: 1,
        last_page: 1
      },
      searchQuery: ''
    }
  },
  computed: {
    filteredCommandes() {
      if (!this.commandes || !Array.isArray(this.commandes)) return []
      if (!this.searchQuery) return this.commandes

      const q = this.searchQuery.toLowerCase()
      return this.commandes.filter(c => {
        const name = (c.name || '').toLowerCase()
        const numFacture = (c.commande?.num_facture || '').toLowerCase()
        return name.includes(q) || numFacture.includes(q)
      })
    }
  },
  async mounted() {
    const auth = useAuthStore()
    await auth.fetchMe()
    this.fetchCommandes()
  },
  methods: {
    async fetchCommandes(page = 1) {
      if (!this.auth.isAuthenticated) {
        console.warn('fetchCommandes annulé : Non authentifié.')
        return
      }
      this.loading = true
      this.error = null
      try {
        const pageNumber = parseInt(page) || 1
        const url = `/commandes?per_page=${this.pagination.per_page || 10}&page=${pageNumber}`
        
        console.log('📡 Fetching commandes from axios:', url)
        
        const response = await axios.get(url)
        const payload = response.data
        console.log('📦 Commandes payload:', JSON.stringify(payload, null, 2))

        if (payload.success) {
          // Gestion flexible : data.data (pagination) ou data direct (array)
          const rawData = payload.data?.data || payload.data || []
          this.commandes = Array.isArray(rawData) ? rawData : []
          
          this.pagination = {
            current_page: parseInt(payload.data?.current_page || payload.current_page) || 1,
            last_page: parseInt(payload.data?.last_page || payload.last_page) || 1
          }
          console.log('✅ Commandes loaded count:', this.commandes.length)
        } else {
          console.warn('⚠️ API success false:', payload.message || 'No message')
        }
      } catch (e) {
        if (e.response?.status === 401) {
          // token expiré ou invalide → logout + redirection vers login
          this.auth.logout()
          this.$router.push('/login')
          return
        }
        this.error = e.response?.data?.message || 'Impossible de récupérer les commandes.'
        console.error('🚨 Erreur fatale chargement commandes:', e)
      } finally {
        this.loading = false
      }
    },
    changePage(page) {
      const pageNumber = parseInt(page)
      if (pageNumber >= 1 && pageNumber <= this.pagination.last_page) {
        this.fetchCommandes(pageNumber)
      }
    },
    stepLabel(step) {
      const labels = {
        'déposé': 'Déposé',
        'en_route': 'En route',
        'arrivé': 'Arrivé',
        'reçu': 'Reçu'
      }
      return labels[step] || step
    },
    stepIcon(step) {
      const icons = {
        'déposé': 'potted_plant',
        'en_route': 'local_shipping',
        'arrivé': 'location_on',
        'reçu': 'task_alt'
      }
      return icons[step] || 'circle'
    }
  }
}
</script>

<style scoped>
.status-chip {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 9999px;
  font-weight: 900;
  font-size: 10px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.status-chip.is-pending {
  background: rgba(148, 163, 184, 0.12);
  color: rgb(71 85 105);
  border: 1px solid rgba(148, 163, 184, 0.22);
}
.status-chip.is-processing {
  background: rgba(26, 34, 127, 0.08);
  color: rgb(26 34 127);
  border: 1px solid rgba(26, 34, 127, 0.15);
}
.status-chip.is-shipped {
  background: rgba(14, 116, 144, 0.1);
  color: rgb(14 116 144);
  border: 1px solid rgba(14, 116, 144, 0.2);
}
.status-chip.is-delivered {
  background: rgba(22, 163, 74, 0.1);
  color: rgb(22 163 74);
  border: 1px solid rgba(22, 163, 74, 0.2);
}
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #cbd5e1;
}

@keyframes toast-progress {
  from { width: 100%; }
  to { width: 0%; }
}

.toast-progress-bar {
  animation: toast-progress 5s linear forwards;
}
</style>
